import type { ReactNode } from 'react';
import { Field } from '../field/field.tsx';
import { Input } from '../input/input.tsx';
import type { JourneyDraft, JourneyFormErrors } from './journey-draft.ts';
import type { JourneyFormLabels } from './journey-form-labels.ts';

export interface JourneyEmployeeFieldsProps {
  draft: JourneyDraft;
  errors: JourneyFormErrors;
  labels: JourneyFormLabels;
  renderRunningJourneyLink?: (journeyId: string) => ReactNode;
  onChange: (field: 'employee_email' | 'employee_name', value: string) => void;
}

/**
 * Who the journey is for. A pair that already has a running journey is refused
 * on the email field, with a link to that journey when the caller offers one.
 */
export function JourneyEmployeeFields({
  draft,
  errors,
  labels,
  renderRunningJourneyLink,
  onChange,
}: JourneyEmployeeFieldsProps) {
  const runningJourneyId = errors.running_journey_id;
  const emailError: ReactNode =
    errors.employee_email ??
    (runningJourneyId === undefined ? undefined : (
      <>
        {labels.errors.alreadyRunning}
        {renderRunningJourneyLink && <> {renderRunningJourneyLink(runningJourneyId)}</>}
      </>
    ));

  return (
    <div className='grid gap-4 mobile:grid-cols-2'>
      <Field
        label={labels.employeeEmail}
        description={labels.employeeEmailDescription}
        error={emailError}
        required
      >
        {(control) => (
          <Input
            {...control}
            name='employee_email'
            type='email'
            autoComplete='off'
            value={draft.employee_email}
            onChange={(event) => onChange('employee_email', event.target.value)}
          />
        )}
      </Field>
      <Field label={labels.employeeName} error={errors.employee_name} required>
        {(control) => (
          <Input
            {...control}
            name='employee_name'
            autoComplete='off'
            value={draft.employee_name}
            onChange={(event) => onChange('employee_name', event.target.value)}
          />
        )}
      </Field>
    </div>
  );
}
